import type { Attachment as WireAttachment, Renditions } from "@theta/api-client";

/**
 * `ready | processing | failed`, taken from the wire rather than restated —
 * a fourth state added on the server must not silently become `never` here.
 */
export type UploadStatus = WireAttachment["status"];

/**
 * COMPOSER ATTACHMENTS — WHAT IS LEFT OF THIS FILE
 *
 * This module used to be the seam between "the text" and "the things attached
 * to it", because the API had no way to say the second thing: an upload
 * reached the server as a `![name|800x600](upload://…)` token inside the body,
 * and every editor therefore had to PARSE those tokens back out of a post's
 * markdown, hold them as an ordered list, and WRITE THEM BACK as a block at
 * the end — with the exact original token preserved so an unusual alt segment
 * survived the round trip.
 *
 * On /api/v1 attachments are ROWS (§1.12): `attachments: [{uploadId}]` in,
 * `attachments: Attachment[]` out, in display order, with renditions. So the
 * whole parse-and-rebuild path is gone:
 *
 *   parsePostBody     deleted — an editor is seeded from `post.attachments`.
 *   TOKEN_RE, kindOf,
 *   nameOf, tidy      deleted with it.
 *   imagePreviewUrl   deleted — `upload://BASE62.ext` is not a url v1 emits.
 *
 * AND THE COMPOSER PATH IS NOW GONE TOO, as this header promised it would be.
 * `buildPostBody`, `attachmentMarkdown` and the `markdown` field on the tray
 * `Attachment` survived only for the DM composer and the event-discussion
 * composer, the last two writers of a legacy body. Both send `attachments`
 * rows now, so all three are deleted (spec line 681):
 *
 *   buildPostBody       deleted — the body IS the body; nothing is appended.
 *   attachmentMarkdown  deleted with it. Its last caller, `useAttachments`,
 *                       was filling a field no reader was left for.
 *   Attachment.markdown deleted — a token has no meaning on the v1 wire.
 *
 * What remains is a TRAY: an ordered list of things being uploaded, what to
 * draw for each, and how to reorder them. No wire format at all.
 */

export type AttachmentKind = "image" | "video" | "file";

export interface Attachment {
  /** Stable key for React while the list is reordered. Never a media url. */
  id: string;
  /**
   * The `uploads` row id. This is what the write path sends (§1.12); it is
   * absent only while the upload is still in flight.
   */
  uploadId?: number;
  kind: AttachmentKind;
  /** Filename, for chips and alt text. */
  name: string;
  /**
   * WHAT THE SERVER IS STILL DOING TO IT — OSO-490.
   *
   * `processing` is a video the worker is encoding; only a video ever gets it
   * (`needsProcessing` is an extension test). The tray shows such a row with a
   * "behandles" badge and DOES NOT BLOCK the composer: the post is sent, and
   * the server publishes it when the transcode lands.
   *
   * `failed` never reaches the tray from an upload — `useAttachments` refuses
   * it at the 201 — but an EDITOR opened on a post whose video failed loads
   * one from the document, and that is the row "Fjern video" removes.
   *
   * Distinct from `uploading`, which is about bytes still going UP.
   */
  status: UploadStatus;
  /**
   * The file's url. Signed, and hour-rounded, for protected-tier content.
   *
   * `null` when there is nothing to serve: a video the worker is still
   * encoding, or one whose transcode failed (OSO-490 §4). An editor opened on
   * a post that carries one keeps the ROW — dropping it here would make an
   * unrelated typo fix silently delete the author's video.
   */
  url: string | null;
  /**
   * Derived sizes — `null` for anything that is not a raster image. A video, an
   * audio file and a 40 MB PDF scan have NO thumb and NO grid, and rendering
   * `renditions.thumb` without checking downloads the original.
   */
  renditions?: Renditions | null;
  /** Object URL for a file picked in this session; preferred for previews. */
  preview?: string;
  /** True while the upload is still in flight. */
  uploading?: boolean;
  /**
   * Upload progress, 0..1, while `uploading` — set only on the Bunny video
   * path (OSO-645), where the bytes go straight to the CDN over TUS and a big
   * file takes long enough that a bar is worth showing. A local upload is one
   * request with no progress events, so it leaves this undefined and the tray
   * shows its indeterminate spinner.
   */
  progress?: number;
}

/**
 * What to show in the tray.
 *
 * The local object URL wins while one exists — it is already in memory, and it
 * is what makes a just-picked photo appear before its upload finishes. The
 * server url is the fallback, and for a protected-tier file it is SIGNED: it is
 * read here at render time and never stored, so it cannot outlive its window.
 */
export function previewSrc(a: Attachment): string | undefined {
  return a.preview ?? a.url ?? undefined;
}

/** Move the item at `from` to `to`, returning a new array. */
export function moveItem<T>(list: T[], from: number, to: number): T[] {
  if (to < 0 || to >= list.length || from === to) return list;
  const next = [...list];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}

/** Which kind an uploaded File belongs to. */
export function kindForFile(file: File): AttachmentKind {
  if (file.type.startsWith("image/")) return "image";
  if (file.type.startsWith("video/")) return "video";
  return "file";
}
